import { DATA_SERVICE_BASE_URL } from '../config/runtime';
import {
  clearTokens,
  getAccessToken,
  getRefreshToken,
  getUserId,
  getSessionId,
  getSessionRevision,
  isOAuthSession,
  isTokenExpiringSoon,
  persistTokens,
} from '../auth/tokenStore';

// Fired when a refresh ultimately fails; the auth context listens and signs out.
export const SESSION_EXPIRED_EVENT = 'scoutcast:session-expired';

// Fired while the backend is being retried through a cold start (Cloud Run
// scale-to-zero). detail.waking is true when retrying, false once recovered.
export const SERVICE_WAKING_EVENT = 'scoutcast:service-waking';

// Transient gateway statuses seen while a scaled-to-zero backend spins up.
const RETRYABLE_STATUS = new Set([502, 503, 504]);
// Exponential backoff between retries (~23s total) to ride out a cold start.
const RETRY_DELAYS_MS = [1000, 2000, 4000, 8000, 8000];

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

function emitWaking(waking: boolean): void {
  window.dispatchEvent(new CustomEvent(SERVICE_WAKING_EVENT, { detail: { waking } }));
}

// Wraps fetch with retry/backoff for cold-start failures: network errors (fetch
// rejects) and 502/503/504. Real HTTP responses (incl. 401/4xx) return as-is.
async function fetchWithRetry(url: string, init: RequestInit): Promise<Response> {
  let signalledWaking = false;
  for (let attempt = 0; ; attempt++) {
    const canRetry = attempt < RETRY_DELAYS_MS.length;
    try {
      const res = await fetch(url, init);
      if (RETRYABLE_STATUS.has(res.status) && canRetry) {
        if (!signalledWaking) {
          signalledWaking = true;
          emitWaking(true);
        }
        await delay(RETRY_DELAYS_MS[attempt]);
        continue;
      }
      if (signalledWaking) emitWaking(false);
      return res;
    } catch (err) {
      if (!canRetry) {
        if (signalledWaking) emitWaking(false);
        throw err;
      }
      if (!signalledWaking) {
        signalledWaking = true;
        emitWaking(true);
      }
      await delay(RETRY_DELAYS_MS[attempt]);
    }
  }
}

let refreshInFlight: Promise<string | null> | null = null;

async function performRefresh(): Promise<string | null> {
  const refreshToken = getRefreshToken();
  const oauth = isOAuthSession();
  const revision = getSessionRevision();
  const userId = getUserId();
  if (!oauth && !refreshToken) return null;
  try {
    const res = await fetchWithRetry(`${DATA_SERVICE_BASE_URL}${oauth ? '/api/auth/oauth/refresh' : '/api/users/refresh-token'}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(oauth ? {} : { refreshToken }),
    });
    if (!res.ok) return null;
    const data = await res.json();
    if (revision !== getSessionRevision() || !data.accessToken || data.requiresMfa) return null;
    if (oauth && userId && data.userId != null && String(data.userId) !== userId) return null;
    persistTokens({
      accessToken: data.accessToken,
      refreshToken: oauth ? undefined : data.refreshToken ?? refreshToken ?? undefined,
      expiresIn: data.expiresIn,
      sessionId: data.sessionId ?? getSessionId() ?? undefined,
      userId: data.userId ?? userId ?? undefined,
      sessionMode: oauth ? 'oauth' : 'legacy',
    });
    return data.accessToken as string;
  } catch {
    return null;
  }
}

// Refreshes if the current token is close to expiry. Concurrent callers share one refresh.
export async function ensureFreshAccessToken(): Promise<string | null> {
  const current = getAccessToken();
  if (current && !isTokenExpiringSoon()) return current;
  const oauth = isOAuthSession();
  const revision = getSessionRevision();
  if (!refreshInFlight) {
    refreshInFlight = performRefresh().finally(() => {
      refreshInFlight = null;
    });
  }
  const refreshed = await refreshInFlight;
  if (!refreshed && oauth && revision === getSessionRevision()) {
    clearTokens();
    window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));
    return null;
  }
  return refreshed ?? getAccessToken();
}

function toUrl(path: string): string {
  if (path.startsWith('http')) return path;
  return `${DATA_SERVICE_BASE_URL}${path.startsWith('/') ? '' : '/'}${path}`;
}

// fetch wrapper that attaches the bearer token, refreshes proactively, and retries
// once on a 401. Returns the raw Response — callers check .ok and parse the body.
export async function fetchWithAuth(path: string, options: RequestInit = {}, _retry = false): Promise<Response> {
  const token = await ensureFreshAccessToken();
  const headers = new Headers(options.headers || {});
  if (!(options.body instanceof FormData) && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  if (token) headers.set('Authorization', `Bearer ${token}`);
  try {
    headers.set('X-Client-Timezone', Intl.DateTimeFormat().resolvedOptions().timeZone);
  } catch {
    /* ignore */
  }

  const res = await fetchWithRetry(toUrl(path), { ...options, headers, credentials: 'include' });

  if (res.status === 401) {
    if (!_retry) {
      if (!refreshInFlight) {
        refreshInFlight = performRefresh().finally(() => { refreshInFlight = null; });
      }
      const refreshed = await refreshInFlight;
      if (refreshed) return fetchWithAuth(path, options, true);
    }
    clearTokens();
    window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));
  }
  return res;
}

// Convenience: fetch + JSON parse, throwing on non-2xx with the server error message.
export async function fetchJson<T>(path: string, options: RequestInit = {}): Promise<T> {
  const res = await fetchWithAuth(path, options);
  const text = await res.text();
  const body = text ? JSON.parse(text) : null;
  if (!res.ok) {
    const message = body?.error || body?.message || `Request failed (${res.status})`;
    const err = new Error(message) as Error & { status?: number };
    err.status = res.status;
    throw err;
  }
  return body as T;
}
